import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from "recharts";
import { LOGO_DARK } from "../components/Navbar";

const STATUS_OPTIONS = ["submitted", "in_review", "verified", "agreement_sent", "closed", "rejected"];

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [intents, setIntents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [quiz, setQuiz] = useState([]);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/intents").then(({ data }) => setIntents(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/kyc").then(({ data }) => setKyc(data)).catch(() => {});
    api.get("/admin/investments").then(({ data }) => setInvestments(data)).catch(() => {});
    api.get("/admin/withdrawals").then(({ data }) => setWithdrawals(data)).catch(() => {});
    api.get("/admin/audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/admin/quiz").then(({ data }) => setQuiz(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/intents/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Failed to update"); }
  };
  const setRisk = async (id, score) => {
    try { await api.put(`/admin/intents/${id}/risk`, { risk_score: Number(score) }); toast.success("Risk scored"); load(); }
    catch { toast.error("Failed"); }
  };
  const decideWithdrawal = async (id, decision) => {
    try { await api.put(`/admin/withdrawals/${id}/decision`, { decision }); toast.success(`Withdrawal ${decision}d`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const runPayouts = async () => {
    try { const { data } = await api.post("/admin/run-payouts"); toast.success(`Payout run (${data.period}): ${data.credited} credited${data.skipped ? `, ${data.skipped} already paid` : ""} · ${formatINR(data.total_disbursed)}`); load(); }
    catch { toast.error("Payout run failed"); }
  };
  const resetData = async () => {
    const phrase = window.prompt("This permanently deletes ALL leads, intents, quiz responses and transactional data (brands & admin are kept). This cannot be undone.\n\nType RESET DATA to confirm:");
    if (phrase === null) return;
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: phrase });
      const d = data.deleted;
      toast.success(`Database cleaned · ${d.users_removed} users, ${d.intents} intents, ${d.quiz_responses} quiz removed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const badge = (s) => ({ paid: "text-emerald-400", pending: "text-amber-400", active: "text-emerald-400", closed: "text-emerald-400", rejected: "text-red-400", requested: "text-amber-400", pending_payment: "text-amber-400" }[s] || "text-ebg-muted");
  const TABS = [["overview", "Overview"], ["intents", "Intents & DD"], ["quiz", "Interest Quiz"], ["kyc", "KYC"], ["leads", "Leads"], ["audit", "Audit Log"]];

  // --- Lead-gen analytics (computed from intents) ---
  const paidIntents = intents.filter((i) => i.payment_status === "paid");
  const interestByBrand = Object.values(intents.reduce((acc, i) => {
    const k = i.brand_name || "—";
    acc[k] = acc[k] || { brand: k, interest: 0, paid: 0, fees: 0 };
    acc[k].interest += 1;
    if (i.payment_status === "paid") { acc[k].paid += 1; acc[k].fees += (i.intent_fee || 0); }
    return acc;
  }, {})).sort((a, b) => b.interest - a.interest);
  const feesByDate = Object.values(paidIntents.reduce((acc, i) => {
    const d = (i.paid_at || i.created_at || "").slice(0, 10);
    if (!d) return acc;
    acc[d] = acc[d] || { date: d, fees: 0 };
    acc[d].fees += (i.intent_fee || 0);
    return acc;
  }, {})).sort((a, b) => a.date.localeCompare(b.date));
  const avgTicket = intents.length ? Math.round(intents.reduce((s, i) => s + (i.ticket_amount || 0), 0) / intents.length) : 0;
  const conversion = stats && stats.total_intents ? Math.round((stats.paid_intents / stats.total_intents) * 100) : 0;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-navy-950">
      <div className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/"><img src={LOGO_DARK} alt="EBG Franchise Partner" className="h-9 w-auto" /></Link>
          <span className="text-xs uppercase tracking-widest text-ebg-muted border-l border-white/10 pl-3">Admin Console</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="text-sm border border-white/15 px-4 py-1.5 rounded-md hover:border-gold hover:text-gold transition-colors">Sign out</button>
        </div>
      </div>

      <div className="px-6 md:px-10 py-8">
        {/* tabs */}
        <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
              className={`text-sm px-5 py-3 border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === v ? "border-gold text-white" : "border-transparent text-ebg-muted hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="admin-overview" className="mt-6 space-y-8">
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats && [
                ["Total Leads", stats.total_leads, "Registered users"],
                ["Showed Interest", stats.total_intents, "Intent applications"],
                ["Paid Intents", stats.paid_intents, "Intent fee paid"],
                ["Conversion", `${conversion}%`, "Interest → paid"],
                ["Intent Fees", formatINR(stats.fees_collected), "Collected"],
                ["Avg Ticket", formatINR(avgTicket), "Per application"],
              ].map(([l, v, sub]) => (
                <div key={l} className="bg-navy-900 border border-white/10 rounded-xl p-4">
                  <p className="text-[0.6rem] uppercase tracking-widest text-ebg-muted">{l}</p>
                  <p className="font-display text-2xl text-white mt-1.5 leading-none">{v}</p>
                  <p className="text-[0.65rem] text-ebg-muted mt-1.5">{sub}</p>
                </div>
              ))}
            </div>

            {/* charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-navy-900 border border-white/10 rounded-xl p-6">
                <h3 className="font-display text-xl text-white">Interest by Brand</h3>
                <p className="text-xs text-ebg-muted mb-4">Number of intent applications per brand</p>
                {interestByBrand.length === 0 ? <p className="text-ebg-muted text-sm py-12 text-center">No data yet.</p> : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={interestByBrand} margin={{ top: 8, right: 8, left: -18, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="brand" tick={{ fill: "#9aa6c2", fontSize: 10 }} angle={-30} textAnchor="end" interval={0} height={60} stroke="rgba(255,255,255,0.1)" />
                      <YAxis allowDecimals={false} tick={{ fill: "#9aa6c2", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                      <Tooltip cursor={{ fill: "rgba(212,175,55,0.06)" }} contentStyle={{ background: "#0f1830", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                      <Bar dataKey="interest" name="Interest" fill="#D4AF37" radius={[4, 4, 0, 0]} maxBarSize={42} />
                      <Bar dataKey="paid" name="Paid" fill="#3f6f55" radius={[4, 4, 0, 0]} maxBarSize={42} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="bg-navy-900 border border-white/10 rounded-xl p-6">
                <h3 className="font-display text-xl text-white">Intent Fees Collected</h3>
                <p className="text-xs text-ebg-muted mb-4">Paid intent fees over time</p>
                {feesByDate.length === 0 ? <p className="text-ebg-muted text-sm py-12 text-center">No fees collected yet.</p> : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={feesByDate} margin={{ top: 8, right: 8, left: 4, bottom: 8 }}>
                      <defs>
                        <linearGradient id="feeFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} tick={{ fill: "#9aa6c2", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                      <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fill: "#9aa6c2", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                      <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: "#0f1830", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                      <Area type="monotone" dataKey="fees" name="Fees" stroke="#D4AF37" strokeWidth={2} fill="url(#feeFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Danger zone */}
            <div className="rounded-xl border border-red-500/30 bg-red-500/[0.04] p-6 mt-2">
              <h3 className="font-display text-xl text-white">Danger Zone</h3>
              <p className="text-sm text-ebg-muted mt-1 max-w-2xl">Permanently remove all test data — leads, intents, quiz responses, and transactional records. Brands and admin accounts are kept. Use this once to start with a clean production database.</p>
              <button onClick={resetData} data-testid="admin-reset-data-btn"
                className="mt-5 text-sm border border-red-500/50 text-red-300 px-5 py-2.5 rounded-md hover:bg-red-500/10 transition-colors">
                Reset All Test Data
              </button>
            </div>
          </div>
        )}

        {tab === "intents" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-intents-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Ref</th><th className="pr-4">Investor</th><th className="pr-4">Brand</th><th className="pr-4">Ticket</th><th className="pr-4">Payment</th><th className="pr-4">Risk</th><th className="pr-4">DD Status</th>
              </tr></thead>
              <tbody>
                {intents.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 font-data text-xs text-gold">{it.ref}</td>
                    <td className="pr-4"><p className="text-white">{it.full_name}</p><p className="text-xs text-ebg-muted">{it.email}</p></td>
                    <td className="pr-4 text-white">{it.brand_name}</td>
                    <td className="pr-4 font-data text-white">{formatINR(it.ticket_amount)}</td>
                    <td className={`pr-4 font-data uppercase text-xs ${badge(it.payment_status)}`}>{it.payment_status}</td>
                    <td className="pr-4">
                      <select value={it.risk_score || ""} onChange={(e) => setRisk(it.id, e.target.value)} data-testid={`admin-risk-${it.ref}`} className="bg-navy-800 border border-white/15 rounded px-2 py-1.5 text-xs text-white focus:border-gold focus:outline-none">
                        <option value="" className="bg-navy-800">—</option>
                        {[1,2,3,4,5].map((n) => <option key={n} value={n} className="bg-navy-800">R{n}</option>)}
                      </select>
                    </td>
                    <td className="pr-4">
                      <select value={it.dd_status} onChange={(e) => updateStatus(it.id, e.target.value)} data-testid={`admin-status-select-${it.ref}`} className="bg-navy-800 border border-white/15 rounded px-2 py-1.5 text-xs text-white focus:border-gold focus:outline-none">
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="bg-navy-800">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {intents.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-ebg-muted">No intents yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "kyc" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-kyc-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Name</th><th className="pr-4">Contact</th><th className="pr-4">PAN</th><th className="pr-4">Aadhaar</th><th className="pr-4">Status</th>
              </tr></thead>
              <tbody>
                {kyc.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 text-white">{u.name}</td>
                    <td className="pr-4 text-ebg-muted">{u.email || u.phone}</td>
                    <td className="pr-4 font-data text-white">{u.kyc_pan || "—"}</td>
                    <td className="pr-4 font-data text-ebg-muted">{u.kyc_aadhaar_masked || "—"}</td>
                    <td className={`pr-4 uppercase text-xs ${u.kyc_status === "verified" ? "text-emerald-400" : "text-ebg-muted"}`}>{u.kyc_status}</td>
                  </tr>
                ))}
                {kyc.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-ebg-muted">No investors yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "investments" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-investments-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Ref</th><th className="pr-4">Brand</th><th className="pr-4">Model</th><th className="pr-4">Principal</th><th className="pr-4">Source</th><th className="pr-4">Status</th><th className="pr-4">Created</th>
              </tr></thead>
              <tbody>
                {investments.map((i) => (
                  <tr key={i.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 font-data text-xs text-gold">{i.ref}</td>
                    <td className="pr-4 text-white">{i.brand_name}</td>
                    <td className="pr-4 text-ebg-muted text-xs">{i.model}</td>
                    <td className="pr-4 font-data text-white">{formatINR(i.principal)}</td>
                    <td className="pr-4 text-ebg-muted text-xs">{i.source}</td>
                    <td className={`pr-4 uppercase text-xs ${badge(i.status)}`}>{i.status}</td>
                    <td className="pr-4 text-ebg-muted font-data text-xs">{i.created_at?.slice(0,10)}</td>
                  </tr>
                ))}
                {investments.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-ebg-muted">No investments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "withdrawals" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-withdrawals-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Ref</th><th className="pr-4">Investor</th><th className="pr-4">Amount</th><th className="pr-4">Status</th><th className="pr-4">Action</th>
              </tr></thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 font-data text-xs text-gold">{w.ref}</td>
                    <td className="pr-4 text-white">{w.user_name}</td>
                    <td className="pr-4 font-data text-white">{formatINR(w.amount)}</td>
                    <td className={`pr-4 uppercase text-xs ${badge(w.status)}`}>{w.status}</td>
                    <td className="pr-4">
                      {w.status === "requested" ? (
                        <div className="flex gap-2">
                          <button onClick={() => decideWithdrawal(w.id, "approve")} data-testid={`wd-approve-${w.ref}`} className="text-xs btn-gold px-3 py-1 rounded">Approve</button>
                          <button onClick={() => decideWithdrawal(w.id, "reject")} data-testid={`wd-reject-${w.ref}`} className="text-xs border border-white/15 text-ebg-muted px-3 py-1 rounded hover:border-red-400 hover:text-red-400">Reject</button>
                        </div>
                      ) : <span className="text-xs text-ebg-muted">{w.decided_by ? `by ${w.decided_by}` : "—"}</span>}
                    </td>
                  </tr>
                ))}
                {withdrawals.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-ebg-muted">No withdrawals yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "quiz" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-quiz-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Name</th><th className="pr-4">Email</th><th className="pr-4">Phone</th><th className="pr-4">Goal</th><th className="pr-4">Budget</th><th className="pr-4">Sectors</th><th className="pr-4">Risk</th><th className="pr-4">Timeline</th><th className="pr-4">Date</th>
              </tr></thead>
              <tbody>
                {quiz.map((q) => (
                  <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
                    <td className="py-3 pr-4 text-white whitespace-nowrap">{q.name}</td>
                    <td className="pr-4 text-ebg-muted">{q.email}</td>
                    <td className="pr-4 text-ebg-muted font-data whitespace-nowrap">{q.phone}</td>
                    <td className="pr-4 text-ebg-muted text-xs">{q.goal || "—"}</td>
                    <td className="pr-4 text-gold text-xs whitespace-nowrap">{q.budget || "—"}</td>
                    <td className="pr-4 text-ebg-muted text-xs max-w-[180px]">{(q.sectors || []).join(", ") || "—"}</td>
                    <td className="pr-4 text-ebg-muted text-xs">{q.risk || "—"}</td>
                    <td className="pr-4 text-ebg-muted text-xs whitespace-nowrap">{q.timeline || "—"}</td>
                    <td className="pr-4 text-ebg-muted font-data text-xs whitespace-nowrap">{q.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {quiz.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-ebg-muted">No questionnaire submissions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Name</th><th className="pr-4">Email</th><th className="pr-4">Phone</th><th className="pr-4">Joined</th>
              </tr></thead>
              <tbody>
                {leads.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 text-white">{u.name}</td>
                    <td className="pr-4 text-ebg-muted">{u.email || "—"}</td>
                    <td className="pr-4 text-ebg-muted font-data">{u.phone || "—"}</td>
                    <td className="pr-4 text-ebg-muted font-data text-xs">{u.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-ebg-muted">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "audit" && (
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm" data-testid="admin-audit-table">
              <thead><tr className="text-left text-[0.6rem] uppercase tracking-widest text-ebg-muted border-b border-white/10">
                <th className="py-3 pr-4">Time</th><th className="pr-4">Actor</th><th className="pr-4">Role</th><th className="pr-4">Action</th><th className="pr-4">IP</th>
              </tr></thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 text-ebg-muted font-data text-xs">{a.ts?.slice(0,19).replace("T"," ")}</td>
                    <td className="pr-4 text-white text-xs">{a.actor_email}</td>
                    <td className="pr-4 text-ebg-muted text-xs">{a.role}</td>
                    <td className="pr-4 font-data text-gold text-xs">{a.action}</td>
                    <td className="pr-4 text-ebg-muted font-data text-xs">{a.ip || "—"}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-ebg-muted">No audit entries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
